import { useMemo, useState } from 'react';
import { Chip } from '../../chip/Chip';
import { EntityList } from '../../entity-list/EntityList';
import { StandaloneModeActionsComponent } from '../../entity-list/interface';
import { Filters } from '../../filter/Filters';
import { ListConductorRawFilter } from '../../list-conductor/filters';
import { useInMemoryFilteredList } from '../../shared/hooks/useInMemoryFilteredList';
import { matchFilters } from '../../shared/utils/filters';
import { USAGE_SUMMARY_SEARCH_FILTER_KEY } from './constants';
import { UsageSummary, UsageSummaryBillingCycle } from './interface';
import { getUsageSummaryFilters } from './usageSummaryFilters';
import { UsageSummaryTable } from './UsageSummaryTable';
import { getFilterKeyMatchValue } from './utils';

export interface UsageSummaryPageProps {
  regions: { label: string; value: string }[];
  dataSource$: () => Promise<[UsageSummary[], UsageSummaryBillingCycle[]]>;
  isMultiRegion: boolean;
  isH20Enabled: boolean;
  StandaloneModeActions: StandaloneModeActionsComponent<object>;
  standaloneModeActionsProps: object;
}

export function UsageSummaryPage({
  regions,
  dataSource$,
  isMultiRegion,
  isH20Enabled,
  StandaloneModeActions,
  standaloneModeActionsProps
}: UsageSummaryPageProps) {
  const [headerStats, setHeaderStats] = useState<UsageSummaryBillingCycle[]>([]);

  const { fetch, reset, filteredList, data } = useInMemoryFilteredList(
    async () => {
      return dataSource$().then(([summaries, stats]) => {
        setHeaderStats(stats);
        return summaries;
      });
    },
    (item, params) =>
      matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterKeyMatchValue(filterKey, item)
      ),
    undefined,
    undefined,
    (item, field) => {
      if (field === 'region') {
        return item.region;
      }

      if (field === 'pipelineType') {
        return item.pipelineType;
      }

      return item.events[parseInt(field.split('events-')[1], 10)];
    }
  );

  const headerStatsWithCount = useMemo(() => {
    if (!headerStats) {
      return [];
    }

    return filteredList.reduce(
      (acc, curr) => {
        curr.events.forEach((e, index) => {
          if (acc[index]) {
            acc[index].count += e;
          }
        });

        return acc;
      },
      headerStats.map(s => ({ ...s, count: 0 }))
    );
  }, [headerStats, filteredList]);

  const filters = useMemo(() => {
    return getUsageSummaryFilters(isH20Enabled, isMultiRegion, regions);
  }, [isH20Enabled, isMultiRegion]);

  return (
    <>
      <div className='flex-col body-container pb-0'>
        <div className='border-bottom pb-5'>
          <div className='center-flex-row pb-1'>
            <span className='text-body-3'>Pipeline Usage Summary</span>
            {isMultiRegion ? (
              <Chip
                size='small'
                dataId='timezone-badge'
                className='ml-1'
                iconName='timezone'
                label='Data as per UTC timezone'
              />
            ) : null}
          </div>

          <span>
            Understand the number of Events loaded through different Pipelines. These events are
            counted against your billing plan.
          </span>
        </div>
      </div>

      <EntityList
        entity='usage-summary'
        dataId=''
        mode='standalone'
        dataSource$={fetch}
        listIcon='graph'
        emptyTitle='No Matching Pipelines Found'
        emptySubTitle='Modify search term to find usage details for the desired pipeline'
        searchKey={USAGE_SUMMARY_SEARCH_FILTER_KEY}
        showSearch
        showFilters
        inMemoryList
        onRefresh={() => {
          reset();
        }}
        FiltersComponent={Filters}
        filtersComponentProps={{
          filters,
          entity: 'usage-summary'
        }}
        shimmerColumnsCount={6}
        newListConnectedTitle='No Pipeline Usage Summary'
        newListConnectedSubTitle='Hevo has not loaded any Event into your Destinations.'
        searchPlaceholder='Search Pipelines'
        genericErrorTitle='Error while fetching Usage Summary'
        genericErrorSubTitle='Retry Loading the Pipelines'
        StandaloneModeActions={StandaloneModeActions}
        standaloneModeActionsProps={{
          ...standaloneModeActionsProps,
          disabled: !data?.length
        }}
        ListComponent={UsageSummaryTable}
        listComponentProps={{
          isMultiRegion,
          isH20Enabled,
          headerStats: headerStatsWithCount
        }}
      />
    </>
  );
}
